import React, { useState } from 'react';
import { Mail, MapPin, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';

const Field = ({ label, name, type = 'text', required, value, onChange, textarea }) => (
  <div>
    <label className="block text-xs tracking-[0.25em] text-[#D4AF7A] font-semibold mb-3">
      {label} {required && '*'}
    </label>
    {textarea ? (
      <textarea
        name={name}
        required={required}
        value={value}
        onChange={onChange}
        rows={5}
        className="w-full rounded-md px-4 py-3 text-sm resize-none"
      />
    ) : (
      <input
        type={type}
        name={name}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full rounded-md px-4 py-3 text-sm"
      />
    )}
  </div>
);

const Contact = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', company: '', subject: '', message: '' });
  const [consentGiven, setConsentGiven] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();

    // Proteção extra além do `disabled` no botão, caso o form seja
    // submetido por outro meio (ex: Enter em algum campo).
    if (!consentGiven) return;

    try {
      const existing = JSON.parse(localStorage.getItem('pezente_leads') || '[]');
      existing.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem('pezente_leads', JSON.stringify(existing));
    } catch (err) {}

    const to = 'contact@pezente.com';
    const subject = form.subject && form.subject.trim().length > 0
      ? form.subject
      : 'Contact from Pezente Digital website';
    const bodyLines = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Company: ${form.company}`,
      `Subject: ${form.subject}`,
      '',
      'Message:',
      form.message,
    ];
    const mailto = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join('\n'))}`;
    window.location.href = mailto;

    toast({ title: t.contact.form.toastOk, description: t.contact.form.toastDesc });
    setForm({ name: '', email: '', company: '', subject: '', message: '' });
    setConsentGiven(false);
  };

  return (
    <section id="contact" className="section-bg py-28 border-t border-[rgba(232,200,136,0.08)]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="dash-title mb-8">— {t.contact.eyebrow} —</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.25rem] light-text leading-[1.25] mb-8 pb-3">
            {t.contact.title}
          </h2>
          <p className="text-[#9CA3AF] text-base leading-relaxed font-light mb-12 max-w-md">
            {t.contact.body}
          </p>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="icon-box w-11 h-11 rounded-md flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs tracking-[0.25em] text-[#D4AF7A] font-semibold mb-1">
                  {t.contact.orWrite}
                </p>
                <a href="mailto:IAM@pezente.com" className="text-[#E8EAF0] text-sm hover:text-[#E8C888] transition-colors">
                  IAM@pezente.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="icon-box w-11 h-11 rounded-md flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs tracking-[0.25em] text-[#D4AF7A] font-semibold mb-1">
                  {t.contact.region}
                </p>
                <p className="text-[#E8EAF0] text-sm">{t.contact.practice}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            className="card-bg rounded-lg p-8 lg:p-10"
            style={{ borderColor: 'rgba(232,200,136,0.15)' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label={t.contact.form.name} name="name" required value={form.name} onChange={onChange} />
              <Field label={t.contact.form.email} name="email" type="email" required value={form.email} onChange={onChange} />
              <Field label={t.contact.form.company} name="company" value={form.company} onChange={onChange} />
              <Field label={t.contact.form.subject} name="subject" value={form.subject} onChange={onChange} />
            </div>
            <div className="mt-6">
              <Field label={t.contact.form.message} name="message" required textarea value={form.message} onChange={onChange} />
            </div>

            {/* Consentimento LGPD/GDPR - obrigatório antes do envio */}
            <div className="mt-6 flex items-start gap-3">
              <input
                type="checkbox"
                id="privacy-consent"
                name="privacyConsent"
                checked={consentGiven}
                onChange={(e) => setConsentGiven(e.target.checked)}
                required
                className="mt-1 w-4 h-4 flex-shrink-0 accent-[#D4AF7A] cursor-pointer"
              />
              <label htmlFor="privacy-consent" className="text-xs text-[#9CA3AF] leading-relaxed cursor-pointer">
                {t.contact.form.consentText}{' '}
                <a
                  href="/privacy-policy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#D4AF7A] underline hover:text-[#E8C888] transition-colors"
                >
                  {t.contact.form.consentLinkText}
                </a>
                .
              </label>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-[#9CA3AF] max-w-xs">{t.contact.form.required}</p>
              <button
                type="submit"
                disabled={!consentGiven}
                className="gold-btn px-8 py-4 text-xs tracking-[0.25em] font-semibold rounded-md flex items-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t.contact.form.submit}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
